import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  addClient,
  updateClient,
  deleteClient,
  deleteAllClients,
  createBlankClient
} from '../../store/slices/clientsSlice';
import { ClientCard } from './components/ClientCard';
import { Client, RetailerClient } from '../../types/client';
import { v4 as uuidv4 } from 'uuid';
import CreateClientModal from './CreateClientModal';

export const Clients: React.FC = () => {
  const dispatch = useAppDispatch();
  const clients = useAppSelector(state => state.clients.clients);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'individual' | 'retailer'>('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get the target client ID from URL
  const targetClientId = searchParams.get('open');

  const handleCreateClient = () => {
    setIsModalOpen(true);
  };

  const handleCreateRetailer = () => {
    const newRetailer: RetailerClient = {
      id: uuidv4(),
      type: 'retailer',
      retailerName: '',
      contactName: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
      preferences: '',
      createdAt: new Date().toISOString()
    };
    dispatch(addClient(newRetailer));
  };

  const handleUpdateClient = (updatedClient: any) => {
    dispatch(updateClient(updatedClient));
  };

  const handleDeleteClient = (id: string) => {
    dispatch(deleteClient(id));
  };

  const handleDeleteAllClients = () => {
    if (clients.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete all ${clients.length} client${clients.length !== 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (confirmed) {
      dispatch(deleteAllClients());
    }
  };

  // Clear search and URL parameter when navigating to a specific client
  useEffect(() => {
    if (targetClientId) {
      // Clear search to ensure client is visible
      setSearchQuery('');

      // Clear URL parameter after a brief delay
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [targetClientId, setSearchParams]);

  // Filter clients based on search query and type filter
  const filteredClients = clients.filter((client: Client) => {
    // Apply type filter
    if (typeFilter !== 'all' && client.type !== typeFilter) {
      return false;
    }

    // Apply search query
    const query = searchQuery.toLowerCase();
    if (!query) {
      return true;
    }

    // Common fields
    const commonMatch =
      client.email.toLowerCase().includes(query) ||
      client.phone.toLowerCase().includes(query) ||
      client.address.toLowerCase().includes(query);

    // Type-specific fields
    if (client.type === 'individual') {
      return commonMatch || client.name.toLowerCase().includes(query);
    } else {
      return (
        commonMatch ||
        client.retailerName.toLowerCase().includes(query) ||
        client.contactName.toLowerCase().includes(query)
      );
    }
  });

  // Sort clients alphabetically by name (individual name or retailer name)
  const sortedClients = [...filteredClients].sort((a, b) => {
    const nameA = (a.type === 'individual' ? a.name : a.retailerName).toLowerCase() || 'zzz';
    const nameB = (b.type === 'individual' ? b.name : b.retailerName).toLowerCase() || 'zzz';
    return nameA.localeCompare(nameB);
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      <CreateClientModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Sticky Header Section */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <header
            style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Clients</h1>
              <p style={{ color: '#666', margin: 0 }}>
                {clients.length} client{clients.length !== 1 ? 's' : ''} total
                {searchQuery && ` (${filteredClients.length} matching)`}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleDeleteAllClients}
                disabled={clients.length === 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: clients.length === 0 ? '#ccc' : '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: clients.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  opacity: clients.length === 0 ? 0.6 : 1
                }}
              >
                Delete All Clients
              </button>

              <button
                onClick={handleCreateClient}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                + Create New Client
              </button>
            </div>
          </header>

          {/* Search Bar and Filter */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search clients by name, email, phone, or address..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '12px 16px',
                fontSize: '1rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                outline: 'none'
              }}
            />
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as 'all' | 'individual' | 'retailer')}
              style={{
                padding: '12px 16px',
                fontSize: '1rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                backgroundColor: 'white',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              <option value="all">All</option>
              <option value="individual">Individuals</option>
              <option value="retailer">Retailers</option>
            </select>
          </div>
        </div>
      </div>

      {/* Scrollable Clients List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {sortedClients.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                color: '#999'
              }}
            >
              {searchQuery ? (
                <>
                  <p style={{ fontSize: '1.2rem', margin: '0 0 12px 0' }}>No clients match your search</p>
                  <p style={{ margin: 0 }}>Try a different search term</p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '1.2rem', margin: '0 0 12px 0' }}>No clients yet</p>
                  <p style={{ margin: 0 }}>Click "+ Create New Client" to get started</p>
                </>
              )}
            </div>
          ) : (
            sortedClients.map(client => (
              <ClientCard
                key={client.id}
                client={client}
                onUpdate={handleUpdateClient}
                onDelete={handleDeleteClient}
                initialExpanded={client.id === targetClientId}
                highlightOnMount={client.id === targetClientId}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
